import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { WorkspaceForm } from './WorkspaceForm';
import { useToast } from '@/shared/hooks/use-toast';
import { useUpdateProduct } from '@/shared/hooks/api/useWorkspace';
import { useOrganization } from '@/shared/hooks/api/useOrganization';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { logger } from '@/infrastructure/lib/logger';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';

interface WorkspaceOverviewTabProps {
  workspaceId: string;
  onSaved?: () => void;
}

export function WorkspaceOverviewTab({ workspaceId, onSaved }: WorkspaceOverviewTabProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync: updateProduct, isPending } = useUpdateProduct();
  const { data: userOrganization } = useOrganization();
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? userOrganization?.organization_id;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formData, setFormData] = useState<any>(null);

  const handleSave = async () => {
    try {
      if (!formData?.name) {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: t('settings.workspaces.errors.nameRequired')
        });
        return;
      }

      if (!effectiveOrgId) {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: t('settings.workspaces.errors.organizationNotFound')
        });
        return;
      }

      await updateProduct({
        id: workspaceId,
        data: {
          name: formData.name,
          description: formData.description,
          website: formData.website,
          organization_id: effectiveOrgId,
          logo_url: formData.logo_url,
          logo: formData.logoFile
        }
      });

      await queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['user-products'] });
      await queryClient.invalidateQueries({ queryKey: ['current-organization-products'] });

      toast({
        title: t('common.success'),
        description: t('settings.workspaces.messages.updateSuccess')
      });
      onSaved?.();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logger.error('Failed to save workspace overview', { error: error?.message, workspaceId });
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: resolveApiErrorMessage(error, t)
      });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('workspaces.overview.title')}</CardTitle>
        <CardDescription>{t('workspaces.overview.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <WorkspaceForm
          key={workspaceId}
          workspaceId={workspaceId}
          onFormDataChange={setFormData}
          hideSubmitButton
          magicFillSize="sm"
        />

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? t('workspaces.overview.saving') : t('workspaces.overview.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
